import React, {Fragment, useCallback, useDeferredValue, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {CenterBox} from "@pg-design/grid-module";
import {FiltersIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import {useIsMounted} from "@pg-mono/hooks";
import type {ICircle, IFitBoundsType, IMarker, IPolygon, IPolygon3D, IPolyline} from "@pg-mono/open-street-map";
import {convertToArrayOfLatLngLiterals, convertToCountryLatLngLiteral, LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {useUserDevice} from "@pg-mono/user-device";

import {osmPublicTileUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {IOfferModalOffer} from "../../../offer/types/OfferModalOffer";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {Country} from "../../../region/types/Country";
import {ViewType} from "../../../view_type/ViewType";
import {setActivePoi, setActivePoiDirections} from "../../actions/set_poi_travel_directions";
import {useOpenStreetMapAlgolyticsTracking} from "../../hooks/use_open_street_map_algolytics_tracking";
import {useOsmPoiRouteMapElements} from "../../hooks/use_osm_poi_route_map_elements";
import {useOSMPoisMapObjects} from "../../hooks/use_osm_pois_map_objects";
import {usePoiMapObjectsState} from "../../hooks/use_poi_map_objects_state";
import {useSinglePoiMarker} from "../../hooks/use_single_poi_marker";
import {IPolygonDefinition} from "../../types/IPolygonDefinition";
import {IPublicTransportRoute} from "../../types/IPublicTransportRoute";
import {PoiType} from "../../types/PoiType";
import {OsmPoiClickHandler} from "../../utils/create_get_osm_poi_marker";
import {MapPlacesModal} from "../MapPlacesModal";
import {OfferInfoWindow} from "../OfferInfoWindow";
import {PoiSwitcher} from "../PoiSwitcher";
import {PoiTravelModeInfoWindow} from "../PoiTravelModeInfoWindow";
import {SinglePoi} from "../SinglePoi";

const offer_pin = require("../../images/pins/offer_pin.svg");

interface IProps {
    viewType: ViewType;
    offer: IOfferDetail | IOfferModalOffer | IPropertyDetailOffer;
    polygon?: {
        coordinates: number[][][];
    };
    region?: number;
    detailPolygons?: IPolygonDefinition[];
    disablePoiSwitch?: boolean;
    disableInitiallyOpenedPoiId?: boolean;
    drawPoiDistance?: boolean;
    onDistanceChange?: (distance: number) => void;
    activePoiRoute?: IPublicTransportRoute | null;
    initialPoiTypes?: PoiType[];
    mobilePoiModalTriggerPosition?: "right" | "center";
    mapConfig?: {
        scrollWheelZoom?: boolean;
        fitBounds?: IFitBoundsType;
        fitBoundsDefaultZoom?: number;
        maxZoom?: number;
    };
    mapBottomSlot?: React.ReactElement | null;
    customPoiMarkers?: IMarker[];
    customMarkers?: IMarker[];
    children?: (props: {
        setCheckedPoiTypes: (checkedPoiTypes: PoiType[]) => void;
        checkedPoiTypes: PoiType[];
        map: React.ReactElement;
        poiDistance: number;
        setPoiDistance: (newDistance: number) => void;
    }) => React.ReactElement;
    clusterMarkers?: boolean;
    className?: string;
    onFullscreenClick?: () => void;
    onPoiClick?: OsmPoiClickHandler;
    onCheckTravelTime?: () => void;
}

export const OpenStreetMapsWithPoi = (props: IProps) => {
    const {
        viewType,
        offer,
        polygon,
        detailPolygons,
        disablePoiSwitch,
        disableInitiallyOpenedPoiId,
        drawPoiDistance,
        onDistanceChange,
        initialPoiTypes,
        mobilePoiModalTriggerPosition,
        mapConfig,
        mapBottomSlot,
        customPoiMarkers,
        customMarkers,
        children,
        clusterMarkers,
        className,
        onFullscreenClick,
        onCheckTravelTime
    } = props;

    const dispatch = useDispatch();
    const {isMobile} = useUserDevice();

    const [showMapPlacesModal, setShowMapPlacesModal] = useState(false);

    const {checkedPoiTypes, onEndPoiEditing, onStartPoiEditing, setCheckedPoiTypes, poiDistance, setPoiDistance} = usePoiMapObjectsState(initialPoiTypes);

    const handleDistanceChange = (newDistance: number) => {
        setPoiDistance(newDistance);
        onDistanceChange?.(newDistance);
    };

    const {userPoiMarkers, poisMarkers, poiDirectionsPolylineCoord} = useOSMPoisMapObjects(offer, {
        disableInitiallyOpenedPoiId,
        checkedPoiTypes,
        radiusInMeters: poiDistance ? poiDistance * 1000 : undefined,
        onPoiClick: props.onPoiClick
    });

    const {markers: singlePoiMarkers, onSinglePoiChange} = useSinglePoiMarker(offer);

    const activePoi = useSelector((state: IRPStore) => state.maps.travelDirections.activePoi);
    const activePoiType = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiType);
    const reduxActivePoiRoute = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiRoute);
    const activePoiRoute = props.activePoiRoute !== undefined ? props.activePoiRoute : reduxActivePoiRoute;

    const osmAlgolyticsTracking = useOpenStreetMapAlgolyticsTracking(viewType);

    const {polyline: activePoiRoutePolyline, markers: activePoiRouteMarkers} = useOsmPoiRouteMapElements(
        offer.geo_point.coordinates,
        activePoiRoute,
        checkedPoiTypes.includes(PoiType.TRANSPORT)
    );

    const offerMarkerFitBounds = useMemo(
        () => ({
            center: convertToCountryLatLngLiteral(offer.geo_point.coordinates || [], offer.region.country),
            radius: 500
        }),
        [offer.geo_point.coordinates[0], offer.geo_point.coordinates[1], offer.region.country]
    );

    // markers
    const createMarkerList = (): IMarker[] => {
        if (offer) {
            return [
                {
                    id: offer.id,
                    coords: convertToCountryLatLngLiteral(offer.geo_point.coordinates, offer.region.country),
                    zIndexOffset: 2,
                    icon: {
                        url: offer_pin,
                        sizes: [30, 38]
                    },
                    popup: () => <OfferInfoWindow offer={offer} />,
                    popupShowCloseButton: true,
                    onClick: () => {
                        dispatch(setActivePoi(null, null));
                        dispatch(setActivePoiDirections(null));
                    }
                }
            ];
        }

        return [];
    };

    const markers: IMarker[] = useMemo(() => {
        const createPoisMarkerList = () => {
            const flatPoiList = checkedPoiTypes.reduce((acc, poiType) => {
                return acc.concat(...(poisMarkers[poiType] || []));
            }, [] as IMarker[]);

            const customPoiMarkersList = (customPoiMarkers || []).filter((marker) => marker.poiType && checkedPoiTypes.includes(marker.poiType));

            return [...flatPoiList, ...customPoiMarkersList];
        };

        return [...activePoiRouteMarkers, ...createMarkerList(), ...createPoisMarkerList(), ...userPoiMarkers, ...singlePoiMarkers, ...(customMarkers || [])];
    }, [
        activePoiRouteMarkers,
        offer.id,
        offer.geo_point.coordinates[0],
        offer.geo_point.coordinates[1],
        checkedPoiTypes,
        userPoiMarkers,
        singlePoiMarkers,
        customMarkers,
        customPoiMarkers,
        poisMarkers
    ]);

    // polygons
    const createPolygonList = () => {
        if ("geo_area" in offer && offer.geo_area) {
            return convertToArrayOfLatLngLiterals(offer.geo_area.coordinates, {reversedValues: true});
        }
    };

    const deferredMarkers = useDeferredValue(markers);

    const polygons: IPolygon[] = useMemo(
        () => [
            {
                id: "default-polygon",
                positions: createPolygonList() || [],

                pathOptions: {
                    fillColor: "#FFCDA5",
                    color: "#23232D",
                    fillOpacity: 0.8,
                    weight: 2
                }
            },
            ...(detailPolygons
                ? detailPolygons.map(
                      (detailPolygon): IPolygon3D => ({
                          id: JSON.stringify(detailPolygon.coords),
                          positions: convertToArrayOfLatLngLiterals(detailPolygon.coords, {reversedValues: true}),
                          height: detailPolygon.height || 1,
                          popup: detailPolygon?.infoWindow?.content
                              ? () => (
                                    <Text variant="headline_4" className={polygonPopupTextStyle}>
                                        {`${detailPolygon?.infoWindow?.content}`}
                                    </Text>
                                )
                              : undefined,
                          pathOptions: {
                              fillColor: detailPolygon.options?.fillColor,
                              color: detailPolygon.options?.strokeColor
                          }
                      })
                  )
                : [])
        ],
        [offer.id, offer.geo_point.coordinates[0], offer.geo_point.coordinates[1], polygon, detailPolygons]
    );

    // polylines
    const polylines: IPolyline[] = useMemo(() => {
        const userPolyline: IPolyline = {
            id: "travel",
            positions: poiDirectionsPolylineCoord,
            pathOptions: {
                color: "var(--colors-danger)",
                weight: 5
            }
        };

        return [userPolyline, ...(activePoiRoutePolyline ? [activePoiRoutePolyline] : [])];
    }, [poiDirectionsPolylineCoord, activePoiRoutePolyline]);

    const circles: ICircle[] | undefined = useMemo(() => {
        return drawPoiDistance
            ? [
                  {
                      center: convertToCountryLatLngLiteral(offer.geo_point.coordinates, offer.region.country),
                      radius: poiDistance * 1000,
                      pathOptions: {
                          color: "#EBFF00",
                          opacity: 0.3
                      }
                  }
              ]
            : [];
    }, [drawPoiDistance, offer.geo_point.coordinates, offer.region.country, poiDistance]);

    const onShowPoiSwitcherModal = () => {
        onStartPoiEditing();
        setShowMapPlacesModal(true);
    };

    const onClosePoiSwitcherModal = (cancelChanges: boolean) => {
        if (cancelChanges) {
            onEndPoiEditing();
        }

        setShowMapPlacesModal(false);
    };

    const isMarkerPopupDisabled = useCallback((marker: IMarker) => isMobile === true && marker.id !== offer.id, [isMobile, offer.id]);

    const map = useMemo(() => {
        return (
            <>
                <LazyOpenStreetMap
                    scrollWheelZoom={mapConfig?.scrollWheelZoom ?? true}
                    fitBounds={mapConfig?.fitBounds}
                    {...osmAlgolyticsTracking}
                    minFitBounds={offerMarkerFitBounds}
                    polygons={polygons}
                    markers={deferredMarkers}
                    polylines={polylines}
                    markerShowPopupOnHover={false}
                    markerDisablePopup={isMarkerPopupDisabled}
                    maxZoom={mapConfig?.maxZoom || 16}
                    clusterMarkers={clusterMarkers}
                    tileUrl={offer.region.country === Country.SPAIN ? osmPublicTileUrl : null}
                    fitBoundsDefaultZoom={mapConfig?.fitBoundsDefaultZoom}
                    circles={circles}
                    onMarkerInvalidCoords={(marker) => {
                        const message = "Invalid marker coords in OpenStreetMapsWithPoi";

                        notifyBugsnagClient(new Error(message), message, JSON.stringify(marker));
                    }}
                    className={className}
                    onFullscreenClick={onFullscreenClick}
                />
            </>
        );
    }, [mapConfig?.scrollWheelZoom, osmAlgolyticsTracking.onMapMove, osmAlgolyticsTracking.onMapInit, polygons, polylines, deferredMarkers]);

    const defaultRenderer = () => (
        <>
            {disablePoiSwitch ? null : (
                <div className={mapPlacesPositionStyle}>
                    <div className={mapPlacesContentStyle}>
                        <PoiSwitcher
                            onChange={setCheckedPoiTypes}
                            checkedPoiTypes={checkedPoiTypes}
                            distanceValue={poiDistance}
                            onDistanceChange={handleDistanceChange}
                            disableCollapsible
                        />

                        <SinglePoi offer={offer} onChange={onSinglePoiChange} onCheckTravelTime={onCheckTravelTime} disableCollapsible />
                    </div>
                </div>
            )}

            {map}
        </>
    );

    const isMounted = useIsMounted();

    if (isMounted && offer) {
        return (
            <div className={mapWrapperStyle}>
                {disablePoiSwitch ? null : (
                    <FiltersIcon
                        wrapperSize="4"
                        size="2.4"
                        wrapperColor="var(--colors-primary)"
                        onClick={onShowPoiSwitcherModal}
                        className={cx(mapPlacesButtonStyle, mobilePoiModalTriggerPosition === "center" && mapPlacesButtonCenteredStyle)}
                    />
                )}

                {children
                    ? children({
                          setCheckedPoiTypes,
                          checkedPoiTypes,
                          poiDistance,
                          setPoiDistance: handleDistanceChange,
                          map
                      })
                    : defaultRenderer()}

                {disablePoiSwitch ? null : (
                    <MapPlacesModal modalState={showMapPlacesModal} onModalClose={onClosePoiSwitcherModal}>
                        <Fragment>
                            <PoiSwitcher
                                onChange={setCheckedPoiTypes}
                                checkedPoiTypes={checkedPoiTypes}
                                distanceValue={poiDistance}
                                onDistanceChange={handleDistanceChange}
                                disableCollapsible
                            />
                            <SinglePoi offer={offer} onChange={onSinglePoiChange} onCheckTravelTime={onCheckTravelTime} disableCollapsible />
                        </Fragment>
                    </MapPlacesModal>
                )}

                <div className={poiTravelModeInfoWindowMobileStyle} id="poiTravelModeInfoWindowMobile">
                    {isMobile && activePoi && activePoiType ? (
                        <PoiTravelModeInfoWindow calcTravelDataOnOpen poi={activePoi} poiType={activePoiType} targetCoords={offer.geo_point.coordinates} />
                    ) : null}
                </div>

                {mapBottomSlot ? <div className={mapBottomSlotStyle}>{mapBottomSlot}</div> : null}
            </div>
        );
    }

    return (
        <CenterBox>
            <Loader size="lg" />
        </CenterBox>
    );
};

const buttonsZIndex = 10;

const polygonPopupTextStyle = css({
    px: 1
});

const mapWrapperStyle = css({
    width: "100%",
    height: "100%",
    position: "relative"
});

const mapPlacesPositionStyle = css({
    position: "absolute",
    top: "0.8rem",
    left: "0.8rem",
    maxHeight: "calc(100% - 1.6rem)",
    zIndex: buttonsZIndex,
    overflow: "hidden",
    display: "none",

    md: {
        display: "flex",
        backgroundColor: "#fff",
        width: "33rem",
        boxShadow: "var(--shadow-1)",
        borderRadius: 2
    }
});

const mapPlacesContentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    width: "100%",
    overflowY: "auto",
    p: 2,

    "&::-webkit-scrollbar": {
        width: "6px"
    },

    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "var(--colors-gray-400)",

        "&:hover": {
            backgroundColor: "var(--colors-gray-500)"
        }
    },

    "&::-webkit-scrollbar-track": {
        backgroundColor: "var(--colors-gray-100)"
    }
});

const mapPlacesButtonStyle = css({
    position: "absolute",
    top: "1.6rem",
    right: "1.6rem",
    zIndex: buttonsZIndex,
    cursor: "pointer",

    md: {
        display: "none"
    }
});

const mapPlacesButtonCenteredStyle = css({
    right: 0,
    left: 0,
    mx: "auto"
});

const poiTravelModeInfoWindowMobileStyle = css({
    position: "absolute",
    bottom: "1.6rem",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 10,
    width: "calc(100% - 3.2rem)",
    pointerEvents: "auto",

    md: {
        display: "none"
    }
});

const mapBottomSlotStyle = css({
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0
});
import React, {Fragment, useCallback, useDeferredValue, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {CenterBox} from "@pg-design/grid-module";
import {FiltersIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import {useIsMounted} from "@pg-mono/hooks";
import type {ICircle, IFitBoundsType, IMarker, IPolygon, IPolygon3D, IPolyline} from "@pg-mono/open-street-map";
import {convertToArrayOfLatLngLiterals, convertToCountryLatLngLiteral, LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {useUserDevice} from "@pg-mono/user-device";

import {osmPublicTileUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {IOfferModalOffer} from "../../../offer/types/OfferModalOffer";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {Country} from "../../../region/types/Country";
import {ViewType} from "../../../view_type/ViewType";
import {setActivePoi, setActivePoiDirections} from "../../actions/set_poi_travel_directions";
import {useOpenStreetMapAlgolyticsTracking} from "../../hooks/use_open_street_map_algolytics_tracking";
import {useOsmPoiRouteMapElements} from "../../hooks/use_osm_poi_route_map_elements";
import {useOSMPoisMapObjects} from "../../hooks/use_osm_pois_map_objects";
import {usePoiMapObjectsState} from "../../hooks/use_poi_map_objects_state";
import {useSinglePoiMarker} from "../../hooks/use_single_poi_marker";
import {IPolygonDefinition} from "../../types/IPolygonDefinition";
import {IPublicTransportRoute} from "../../types/IPublicTransportRoute";
import {PoiType} from "../../types/PoiType";
import {OsmPoiClickHandler} from "../../utils/create_get_osm_poi_marker";
import {MapPlacesModal} from "../MapPlacesModal";
import {OfferInfoWindow} from "../OfferInfoWindow";
import {PoiSwitcher} from "../PoiSwitcher";
import {PoiTravelModeInfoWindow} from "../PoiTravelModeInfoWindow";
import {SinglePoi} from "../SinglePoi";

const offer_pin = require("../../images/pins/offer_pin.svg");

interface IProps {
    viewType: ViewType;
    offer: IOfferDetail | IOfferModalOffer | IPropertyDetailOffer;
    polygon?: {
        coordinates: number[][][];
    };
    region?: number;
    detailPolygons?: IPolygonDefinition[];
    disablePoiSwitch?: boolean;
    disableInitiallyOpenedPoiId?: boolean;
    drawPoiDistance?: boolean;
    onDistanceChange?: (distance: number) => void;
    activePoiRoute?: IPublicTransportRoute | null;
    initialPoiTypes?: PoiType[];
    mobilePoiModalTriggerPosition?: "right" | "center";
    mapConfig?: {
        scrollWheelZoom?: boolean;
        fitBounds?: IFitBoundsType;
        fitBoundsDefaultZoom?: number;
        maxZoom?: number;
    };
    mapBottomSlot?: React.ReactElement | null;
    customPoiMarkers?: IMarker[];
    customMarkers?: IMarker[];
    children?: (props: {
        setCheckedPoiTypes: (checkedPoiTypes: PoiType[]) => void;
        checkedPoiTypes: PoiType[];
        map: React.ReactElement;
        poiDistance: number;
        setPoiDistance: (newDistance: number) => void;
    }) => React.ReactElement;
    clusterMarkers?: boolean;
    className?: string;
    onFullscreenClick?: () => void;
    onPoiClick?: OsmPoiClickHandler;
    onCheckTravelTime?: () => void;
}

export const OpenStreetMapsWithPoi = (props: IProps) => {
    const {
        viewType,
        offer,
        polygon,
        detailPolygons,
        disablePoiSwitch,
        disableInitiallyOpenedPoiId,
        drawPoiDistance,
        onDistanceChange,
        initialPoiTypes,
        mobilePoiModalTriggerPosition,
        mapConfig,
        mapBottomSlot,
        customPoiMarkers,
        customMarkers,
        children,
        clusterMarkers,
        className,
        onFullscreenClick,
        onCheckTravelTime
    } = props;

    const dispatch = useDispatch();
    const {isMobile} = useUserDevice();

    const [showMapPlacesModal, setShowMapPlacesModal] = useState(false);

    const {checkedPoiTypes, onEndPoiEditing, onStartPoiEditing, setCheckedPoiTypes, poiDistance, setPoiDistance} = usePoiMapObjectsState(initialPoiTypes);

    const handleDistanceChange = (newDistance: number) => {
        setPoiDistance(newDistance);
        onDistanceChange?.(newDistance);
    };

    const {userPoiMarkers, poisMarkers, poiDirectionsPolylineCoord} = useOSMPoisMapObjects(offer, {
        disableInitiallyOpenedPoiId,
        checkedPoiTypes,
        radiusInMeters: poiDistance ? poiDistance * 1000 : undefined,
        onPoiClick: props.onPoiClick
    });

    const {markers: singlePoiMarkers, onSinglePoiChange} = useSinglePoiMarker(offer);

    const activePoi = useSelector((state: IRPStore) => state.maps.travelDirections.activePoi);
    const activePoiType = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiType);
    const reduxActivePoiRoute = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiRoute);
    const activePoiRoute = props.activePoiRoute !== undefined ? props.activePoiRoute : reduxActivePoiRoute;

    const osmAlgolyticsTracking = useOpenStreetMapAlgolyticsTracking(viewType);

    const {polyline: activePoiRoutePolyline, markers: activePoiRouteMarkers} = useOsmPoiRouteMapElements(
        offer.geo_point.coordinates,
        activePoiRoute,
        checkedPoiTypes.includes(PoiType.TRANSPORT)
    );

    const offerMarkerFitBounds = useMemo(
        () => ({
            center: convertToCountryLatLngLiteral(offer.geo_point.coordinates || [], offer.region.country),
            radius: 500
        }),
        [offer.geo_point.coordinates[0], offer.geo_point.coordinates[1], offer.region.country]
    );

    // markers
    const createMarkerList = (): IMarker[] => {
        if (offer) {
            return [
                {
                    id: offer.id,
                    coords: convertToCountryLatLngLiteral(offer.geo_point.coordinates, offer.region.country),
                    zIndexOffset: 2,
                    icon: {
                        url: offer_pin,
                        sizes: [30, 38]
                    },
                    popup: () => <OfferInfoWindow offer={offer} />,
                    popupShowCloseButton: true,
                    onClick: () => {
                        dispatch(setActivePoi(null, null));
                        dispatch(setActivePoiDirections(null));
                    }
                }
            ];
        }

        return [];
    };

    const markers: IMarker[] = useMemo(() => {
        const createPoisMarkerList = () => {
            const flatPoiList = checkedPoiTypes.reduce((acc, poiType) => {
                return acc.concat(...(poisMarkers[poiType] || []));
            }, [] as IMarker[]);

            const customPoiMarkersList = (customPoiMarkers || []).filter((marker) => marker.poiType && checkedPoiTypes.includes(marker.poiType));

            return [...flatPoiList, ...customPoiMarkersList];
        };

        return [...activePoiRouteMarkers, ...createMarkerList(), ...createPoisMarkerList(), ...userPoiMarkers, ...singlePoiMarkers, ...(customMarkers || [])];
    }, [
        activePoiRouteMarkers,
        offer.id,
        offer.geo_point.coordinates[0],
        offer.geo_point.coordinates[1],
        checkedPoiTypes,
        userPoiMarkers,
        singlePoiMarkers,
        customMarkers,
        customPoiMarkers,
        poisMarkers
    ]);

    // polygons
    const createPolygonList = () => {
        if ("geo_area" in offer && offer.geo_area) {
            return convertToArrayOfLatLngLiterals(offer.geo_area.coordinates, {reversedValues: true});
        }
    };

    const deferredMarkers = useDeferredValue(markers);

    const polygons: IPolygon[] = useMemo(
        () => [
            {
                id: "default-polygon",
                positions: createPolygonList() || [],

                pathOptions: {
                    fillColor: "#FFCDA5",
                    color: "#23232D",
                    fillOpacity: 0.8,
                    weight: 2
                }
            },
            ...(detailPolygons
                ? detailPolygons.map(
                      (detailPolygon): IPolygon3D => ({
                          id: JSON.stringify(detailPolygon.coords),
                          positions: convertToArrayOfLatLngLiterals(detailPolygon.coords, {reversedValues: true}),
                          height: detailPolygon.height || 1,
                          popup: detailPolygon?.infoWindow?.content
                              ? () => (
                                    <Text variant="headline_4" className={polygonPopupTextStyle}>
                                        {`${detailPolygon?.infoWindow?.content}`}
                                    </Text>
                                )
                              : undefined,
                          pathOptions: {
                              fillColor: detailPolygon.options?.fillColor,
                              color: detailPolygon.options?.strokeColor
                          }
                      })
                  )
                : [])
        ],
        [offer.id, offer.geo_point.coordinates[0], offer.geo_point.coordinates[1], polygon, detailPolygons]
    );

    // polylines
    const polylines: IPolyline[] = useMemo(() => {
        const userPolyline: IPolyline = {
            id: "travel",
            positions: poiDirectionsPolylineCoord,
            pathOptions: {
                color: "var(--colors-danger)",
                weight: 5
            }
        };

        return [userPolyline, ...(activePoiRoutePolyline ? [activePoiRoutePolyline] : [])];
    }, [poiDirectionsPolylineCoord, activePoiRoutePolyline]);

    const circles: ICircle[] | undefined = useMemo(() => {
        return drawPoiDistance
            ? [
                  {
                      center: convertToCountryLatLngLiteral(offer.geo_point.coordinates, offer.region.country),
                      radius: poiDistance * 1000,
                      pathOptions: {
                          color: "#EBFF00",
                          opacity: 0.3
                      }
                  }
              ]
            : [];
    }, [drawPoiDistance, offer.geo_point.coordinates, offer.region.country, poiDistance]);

    const onShowPoiSwitcherModal = () => {
        onStartPoiEditing();
        setShowMapPlacesModal(true);
    };

    const onClosePoiSwitcherModal = (cancelChanges: boolean) => {
        if (cancelChanges) {
            onEndPoiEditing();
        }

        setShowMapPlacesModal(false);
    };

    const isMarkerPopupDisabled = useCallback((marker: IMarker) => isMobile === true && marker.id !== offer.id, [isMobile, offer.id]);

    const map = useMemo(() => {
        return (
            <>
                <LazyOpenStreetMap
                    scrollWheelZoom={mapConfig?.scrollWheelZoom ?? true}
                    fitBounds={mapConfig?.fitBounds}
                    {...osmAlgolyticsTracking}
                    minFitBounds={offerMarkerFitBounds}
                    polygons={polygons}
                    markers={deferredMarkers}
                    polylines={polylines}
                    markerShowPopupOnHover={false}
                    markerDisablePopup={isMarkerPopupDisabled}
                    maxZoom={mapConfig?.maxZoom || 16}
                    clusterMarkers={clusterMarkers}
                    tileUrl={offer.region.country === Country.SPAIN ? osmPublicTileUrl : null}
                    fitBoundsDefaultZoom={mapConfig?.fitBoundsDefaultZoom}
                    circles={circles}
                    onMarkerInvalidCoords={(marker) => {
                        const message = "Invalid marker coords in OpenStreetMapsWithPoi";

                        notifyBugsnagClient(new Error(message), message, JSON.stringify(marker));
                    }}
                    className={className}
                    onFullscreenClick={onFullscreenClick}
                />
            </>
        );
    }, [mapConfig?.scrollWheelZoom, osmAlgolyticsTracking.onMapMove, osmAlgolyticsTracking.onMapInit, polygons, polylines, deferredMarkers]);

    const defaultRenderer = () => (
        <>
            {disablePoiSwitch ? null : (
                <div className={mapPlacesPositionStyle}>
                    <div className={mapPlacesContentStyle}>
                        <PoiSwitcher
                            onChange={setCheckedPoiTypes}
                            checkedPoiTypes={checkedPoiTypes}
                            distanceValue={poiDistance}
                            onDistanceChange={handleDistanceChange}
                            disableCollapsible
                        />

                        <SinglePoi offer={offer} onChange={onSinglePoiChange} onCheckTravelTime={onCheckTravelTime} disableCollapsible />
                    </div>
                </div>
            )}

            {map}
        </>
    );

    const isMounted = useIsMounted();

    if (isMounted && offer) {
        return (
            <div className={mapWrapperStyle}>
                {disablePoiSwitch ? null : (
                    <FiltersIcon
                        wrapperSize="4"
                        size="2.4"
                        wrapperColor="var(--colors-primary)"
                        onClick={onShowPoiSwitcherModal}
                        className={cx(mapPlacesButtonStyle, mobilePoiModalTriggerPosition === "center" && mapPlacesButtonCenteredStyle)}
                    />
                )}

                {children
                    ? children({
                          setCheckedPoiTypes,
                          checkedPoiTypes,
                          poiDistance,
                          setPoiDistance: handleDistanceChange,
                          map
                      })
                    : defaultRenderer()}

                {disablePoiSwitch ? null : (
                    <MapPlacesModal modalState={showMapPlacesModal} onModalClose={onClosePoiSwitcherModal}>
                        <Fragment>
                            <PoiSwitcher
                                onChange={setCheckedPoiTypes}
                                checkedPoiTypes={checkedPoiTypes}
                                distanceValue={poiDistance}
                                onDistanceChange={handleDistanceChange}
                                disableCollapsible
                            />
                            <SinglePoi offer={offer} onChange={onSinglePoiChange} onCheckTravelTime={onCheckTravelTime} disableCollapsible />
                        </Fragment>
                    </MapPlacesModal>
                )}

                <div className={poiTravelModeInfoWindowMobileStyle} id="poiTravelModeInfoWindowMobile">
                    {isMobile && activePoi && activePoiType ? (
                        <PoiTravelModeInfoWindow calcTravelDataOnOpen poi={activePoi} poiType={activePoiType} targetCoords={offer.geo_point.coordinates} />
                    ) : null}
                </div>

                {mapBottomSlot ? <div className={mapBottomSlotStyle}>{mapBottomSlot}</div> : null}
            </div>
        );
    }

    return (
        <CenterBox>
            <Loader size="lg" />
        </CenterBox>
    );
};

const buttonsZIndex = 10;

const polygonPopupTextStyle = css({
    px: 1
});

const mapWrapperStyle = css({
    width: "100%",
    height: "100%",
    position: "relative"
});

const mapPlacesPositionStyle = css({
    position: "absolute",
    top: "0.8rem",
    left: "0.8rem",
    maxHeight: "calc(100% - 1.6rem)",
    zIndex: buttonsZIndex,
    overflow: "hidden",
    display: "none",

    md: {
        display: "flex",
        backgroundColor: "#fff",
        width: "33rem",
        boxShadow: "var(--shadow-1)",
        borderRadius: 2
    }
});

const mapPlacesContentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    width: "100%",
    overflowY: "auto",
    p: 2,

    "&::-webkit-scrollbar": {
        width: "6px"
    },

    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "var(--colors-gray-400)",

        "&:hover": {
            backgroundColor: "var(--colors-gray-500)"
        }
    },

    "&::-webkit-scrollbar-track": {
        backgroundColor: "var(--colors-gray-100)"
    }
});

const mapPlacesButtonStyle = css({
    position: "absolute",
    top: "1.6rem",
    right: "1.6rem",
    zIndex: buttonsZIndex,
    cursor: "pointer",

    md: {
        display: "none"
    }
});

const mapPlacesButtonCenteredStyle = css({
    right: 0,
    left: 0,
    mx: "auto"
});

const poiTravelModeInfoWindowMobileStyle = css({
    position: "absolute",
    bottom: "1.6rem",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 10,
    width: "calc(100% - 3.2rem)",
    pointerEvents: "auto",

    md: {
        display: "none"
    }
});

const mapBottomSlotStyle = css({
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0
});
